import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Check, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { WidgetPreviewCard } from "@/components/WidgetPreviewCard";
import { type DayKey, dayKeyFromIndex } from "@/lib/days";
import { groupByRow, moveWidget, parseDropId, rebalance } from "@/lib/layout";
import type { WidgetConfig } from "@/lib/schema";

interface Props {
  widgets: WidgetConfig[];
  onSave: (next: WidgetConfig[]) => Promise<void> | void;
  onCancel: () => void;
}

function todayDayKey(): DayKey {
  const idx = (new Date().getDay() + 6) % 7;
  return dayKeyFromIndex(idx);
}

export function LayoutEditor({ widgets, onSave, onCancel }: Props) {
  const [draft, setDraft] = useState<WidgetConfig[]>(widgets);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const rows = useMemo(() => groupByRow(draft), [draft]);
  const selectedDay = useMemo(() => todayDayKey(), []);

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    const target = parseDropId(String(e.over.id));
    if (!target) return;
    setDraft((d) => moveWidget(d, String(e.active.id), target));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(rebalance(draft));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="layout-editor" data-testid="layout-editor">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <NewRowDropZone row={0} />
        {rows.map((r, rowIdx) => (
          <Fragment key={r.row}>
            <div
              className="layout-row"
              style={{
                gridTemplateColumns: `repeat(${r.widgets.length}, var(--pad) 1fr) var(--pad)`,
              }}
              data-testid={`layout-row-${r.row}`}
            >
              <SlotDropZone row={r.row} column={0} orientation="vertical" />
              {r.widgets.map((w, colIdx) => (
                <Fragment key={w.id}>
                  <DraggablePreview
                    widget={w}
                    selectedDay={selectedDay}
                    allWidgets={draft}
                  />
                  <SlotDropZone
                    row={r.row}
                    column={colIdx + 1}
                    orientation="vertical"
                  />
                </Fragment>
              ))}
            </div>
            <NewRowDropZone row={rowIdx + 1} />
          </Fragment>
        ))}
      </DndContext>
      <div className="layout-edit-bar">
        <button
          type="button"
          className="btn ghost icon-btn"
          onClick={onCancel}
          data-testid="layout-edit-cancel"
          disabled={saving}
        >
          <X size={16} />
          Avbryt
        </button>
        <button
          type="button"
          className="btn icon-btn"
          onClick={handleSave}
          data-testid="layout-edit-save"
          disabled={saving}
        >
          <Check size={16} />
          Lagre
        </button>
      </div>
    </div>
  );
}

function DraggablePreview({
  widget,
  selectedDay,
  allWidgets,
}: {
  widget: WidgetConfig;
  selectedDay: DayKey;
  allWidgets: WidgetConfig[];
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: widget.id });
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;
  return (
    <WidgetPreviewCard
      ref={setNodeRef}
      widget={widget}
      selectedDay={selectedDay}
      allWidgets={allWidgets}
      editing
      className={isDragging ? "dragging" : undefined}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
}

function SlotDropZone({
  row,
  column,
  orientation,
}: {
  row: number;
  column: number;
  orientation: "vertical" | "horizontal";
}) {
  const id = `slot-row-${row}-col-${column}`;
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`drop-zone drop-zone-${orientation} ${isOver ? "over" : ""}`}
      data-testid={id}
    />
  );
}

function NewRowDropZone({ row }: { row: number }) {
  const id = `new-row-${row}`;
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`drop-zone drop-zone-row ${isOver ? "over" : ""}`}
      data-testid={id}
    />
  );
}
